import { processEnv } from 'components/configuration/environmentVariables'
import { NormalizedDocumentTemplateViewItem } from './model'

/**
 * If the given clipboard event is a paste of HTML paragraph(s)
 * (e.g., paras copied from Word), calls the given event handler and also
 *  calls `event.preventDefault()` and `event.stopPropogation()`.
 * @param handleHtmlParagraphPaste Handler to call.
 * @param event Clipboard event to get data from. If the handler function
 * is called, `event.preventDefault()` and `event.stopPropogation()` are called.
 * @param template The template view item representing the current paragraph.
 * @returns `true` if clipboard contained HTML para(s) and handler function was called.
 */
export const tryHandleHtmlParagraphPaste = (
    handleHtmlParagraphPaste: (pastedHtml: string) => void,
    event: React.ClipboardEvent<HTMLDivElement>,
    template: NormalizedDocumentTemplateViewItem | undefined
) => {
    if (processEnv.REACT_APP_EnablePasteFromWord !== '1') {
        // Feature is turned off
        return false
    }

    if (!handleHtmlParagraphPaste) {
        return false
    }

    if (template?.addingState !== 'newlyAdded') {
        // Don't allow paste within existing items (future enhancement)
        return false
    }

    const clipboardHtml = event.clipboardData.getData('text/html')
    if (
        clipboardHtml &&
        clipboardHtml.indexOf('<p') !== -1 && // must include at least one full paragraph (otherwise should be handled using our standard paste)
        event.clipboardData.types.some(
            (type) => type?.toLowerCase().trim() === 'text/html' // must include html data (e.g., paragraphs from Word)
        )
    ) {
        // HTML paragraphs are being pasted; handle them using
        // the provided handler function (e.g., so we can call the API to process them)
        handleHtmlParagraphPaste(clipboardHtml)
        event.preventDefault()
        event.stopPropagation()
        return true
    }

    return false
}
